import React, { useState, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { registerHousing, getProvinces, getCities } from "../services/api";

const TOTAL_STEPS = 4;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const INITIAL_FORM = {
  name: "",
  description: "",
  address: "",
  cityId: "",
  cityName: "",
  provinceId: "",
  provinceName: "",
  unit: "",
  houseType: "",
  welcomeMessage: "",
  mapUrl: "",
  longitude: "",
  latitude: "",
  logoFile: null,
  npwpFile: null,
  imgFiles: [null],
  legalDocFiles: [null],
  picName: "",
  picNik: "",
  picEmail: "",
  picPhone: "",
  picIdImgFile: null,
  bsiAccountOwned: false,
};

const STEP_TITLES = [
  "Informasi Perumahan",
  "Media & Dokumen",
  "Data PIC",
  "Konfirmasi",
];

const validateFileSize = (file) => {
  if (!file) return null;
  if (file.size > MAX_FILE_SIZE) {
    return `File "${file.name}" melebihi batas maksimal 5 MB.`;
  }
  return null;
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

// ─── Validation ───────────────────────────────────────────────────────────────
const validateStep = (step, form) => {
  const errors = {};
  if (step === 1) {
    if (!form.name.trim()) errors.name = "Nama perumahan wajib diisi.";
    if (!form.description.trim()) errors.description = "Deskripsi wajib diisi.";
    if (!form.address.trim()) errors.address = "Alamat wajib diisi.";
    if (!form.provinceId) errors.province = "Provinsi wajib dipilih.";
    if (!form.cityId) errors.city = "Kota wajib dipilih.";
    if (!form.unit.trim()) errors.unit = "Jumlah unit wajib diisi.";
    if (!form.houseType.trim()) errors.houseType = "Tipe rumah wajib diisi.";
  }
  if (step === 2) {
    if (!form.logoFile) errors.logoFile = "File logo wajib diunggah.";
    // if (!form.npwpFile) errors.npwpFile = "File NPWP wajib diunggah.";
    if (form.imgFiles.some((el) => !el))
      errors.imgFiles = "Semua file gambar harus diunggah.";
    if (form.legalDocFiles.some((el) => !el))
      errors.legalDocFiles = "Semua file dokumen legal harus diunggah.";
  }
  if (step === 3) {
    if (!form.picName.trim()) errors.picName = "Nama PIC wajib diisi.";
    if (!form.picNik.trim()) errors.picNik = "NIK PIC wajib diisi.";
    if (!form.picEmail.trim()) errors.picEmail = "Email PIC wajib diisi.";
    else if (!/\S+@\S+\.\S+/.test(form.picEmail))
      errors.picEmail = "Format email tidak valid.";
    if (!form.picPhone.trim()) errors.picPhone = "Nomor HP PIC wajib diisi.";
    if (!form.picIdImgFile)
      errors.picIdImgFile = "File foto KTP PIC wajib diunggah.";
  }
  return errors;
};

const Field = ({ label, error, children, required }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-sm font-semibold" style={{ color: "#005F5A" }}>
      {label} {required && <span style={{ color: "#00BBA7" }}>*</span>}
    </label>
    {children}
    {error && <p className="text-xs text-red-500">{error}</p>}
  </div>
);

const Input = ({ error, ...props }) => (
  <input
    {...props}
    style={{
      background: "white",
      border: `2px solid ${error ? "#f87171" : "#96F7E4"}`,
      color: "#0B4F4A",
    }}
    className="rounded-xl px-4 py-3 text-sm focus:outline-none transition-all w-full"
    onFocus={(e) => {
      e.target.style.borderColor = "#00BBA7";
      e.target.style.boxShadow = "0 0 0 3px #CBFBF1";
    }}
    onBlur={(e) => {
      e.target.style.borderColor = error ? "#f87171" : "#96F7E4";
      e.target.style.boxShadow = "none";
    }}
  />
);

const Textarea = ({ error, rows = 3, ...props }) => (
  <textarea
    {...props}
    rows={rows}
    style={{
      background: "white",
      border: `2px solid ${error ? "#f87171" : "#96F7E4"}`,
      color: "#0B4F4A",
      resize: "none",
    }}
    className="rounded-xl px-4 py-3 text-sm focus:outline-none transition-all w-full"
    onFocus={(e) => {
      e.target.style.borderColor = "#00BBA7";
      e.target.style.boxShadow = "0 0 0 3px #CBFBF1";
    }}
    onBlur={(e) => {
      e.target.style.borderColor = error ? "#f87171" : "#96F7E4";
      e.target.style.boxShadow = "none";
    }}
  />
);

const SearchableSelect = ({
  label,
  required,
  error,
  value,
  placeholder,
  onSearch,
  onSelect,
  options,
  loading,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    onSearch(val);
    setOpen(true);
  };

  const handleSelect = (item) => {
    onSelect(item);
    setQuery(item.name);
    setOpen(false);
  };

  const handleOpen = () => {
    if (!disabled) {
      setOpen(true);
      onSearch(query);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  useEffect(() => {
    if (!value) setQuery("");
  }, [value]);

  return (
    <Field label={label} error={error} required={required}>
      <div ref={wrapperRef} className="relative">
        <div
          className="flex items-center rounded-xl px-4 py-3 cursor-text transition-all"
          style={{
            background: disabled ? "#F0FDFA" : "white",
            border: `2px solid ${error ? "#f87171" : open ? "#00BBA7" : "#96F7E4"}`,
            boxShadow: open ? "0 0 0 3px #CBFBF1" : "none",
          }}
          onClick={handleOpen}
        >
          <input
            ref={inputRef}
            value={query || (value && !open ? value : query)}
            onChange={handleInputChange}
            onFocus={() => {
              setOpen(true);
              onSearch(query);
            }}
            placeholder={disabled ? "Pilih provinsi dulu" : placeholder}
            disabled={disabled}
            className="flex-1 text-sm focus:outline-none bg-transparent"
            style={{ color: "#0B4F4A" }}
          />
          <div className="shrink-0 ml-2">
            {loading ? (
              <svg
                className="w-4 h-4 animate-spin"
                style={{ color: "#00BBA7" }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            ) : value && !open ? (
              <svg
                className="w-4 h-4"
                style={{ color: "#00BBA7" }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            ) : (
              <svg
                className="w-4 h-4"
                style={{ color: "#96F7E4" }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            )}
          </div>
        </div>
        {open && (
          <div
            className="absolute z-50 w-full mt-1.5 rounded-xl overflow-hidden shadow-xl"
            style={{
              border: "1.5px solid #CBFBF1",
              background: "white",
              maxHeight: "220px",
              overflowY: "auto",
            }}
          >
            {loading ? (
              <div
                className="px-4 py-3 text-sm text-center"
                style={{ color: "#009689" }}
              >
                Mencari...
              </div>
            ) : options.length === 0 ? (
              <div
                className="px-4 py-3 text-sm text-center"
                style={{ color: "#96F7E4" }}
              >
                {query ? "Tidak ditemukan" : "Ketik untuk mencari..."}
              </div>
            ) : (
              options.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onMouseDown={() => handleSelect(item)}
                  className="w-full text-left px-4 py-2.5 text-sm transition-colors"
                  style={{ color: "#0B4F4A" }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = "#F0FDFA";
                    e.currentTarget.style.color = "#005F5A";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = "white";
                    e.currentTarget.style.color = "#0B4F4A";
                  }}
                >
                  {item.name}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </Field>
  );
};

const FileInput = ({
  label,
  accept,
  error,
  file,
  onChange,
  hint,
  required,
}) => (
  <Field label={label} error={error} required={required}>
    <label
      className="flex items-center gap-3 rounded-xl px-4 py-3 cursor-pointer transition-all"
      style={{
        background: "white",
        border: `2px dashed ${error ? "#f87171" : file ? "#00BBA7" : "#96F7E4"}`,
      }}
      onMouseOver={(e) => (e.currentTarget.style.borderColor = "#00BBA7")}
      onMouseOut={(e) =>
        (e.currentTarget.style.borderColor = error
          ? "#f87171"
          : file
            ? "#00BBA7"
            : "#96F7E4")
      }
    >
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
        style={{ background: file ? "#CBFBF1" : "#F0FDFA" }}
      >
        {file ? (
          <svg
            className="w-5 h-5"
            style={{ color: "#009689" }}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        ) : (
          <svg
            className="w-5 h-5"
            style={{ color: "#96F7E4" }}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
            />
          </svg>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p
          className="text-sm font-medium truncate"
          style={{ color: file ? "#005F5A" : "#96F7E4" }}
        >
          {file ? file.name : "Klik untuk pilih file"}
        </p>
        {hint && !file && (
          <p className="text-xs mt-0.5" style={{ color: "#96F7E4" }}>
            {hint}
          </p>
        )}
        {file && (
          <p
            className="text-xs mt-0.5"
            style={{ color: file.size > MAX_FILE_SIZE ? "#ef4444" : "#009689" }}
          >
            {formatFileSize(file.size)}
            {file.size > MAX_FILE_SIZE ? " — melebihi batas 5 MB!" : ""}
          </p>
        )}
      </div>
      {/* Size badge */}
      <div className="flex flex-col items-end gap-1 shrink-0">
        {file && (
          <span className="text-xs font-semibold" style={{ color: "#00BBA7" }}>
            Ganti
          </span>
        )}
        <span
          className="text-xs px-1.5 py-0.5 rounded-md font-medium"
          style={{
            background: "#F0FDFA",
            color: "#009689",
            border: "1px solid #96F7E4",
          }}
        >
          maks. 5 MB
        </span>
      </div>
      <input
        type="file"
        accept={accept}
        className="hidden"
        onChange={onChange}
      />
    </label>
  </Field>
);

const PreviewModal = ({ file, onClose }) => {
  const [url, setUrl] = useState(null);
  const isImage = file?.type?.startsWith("image/");
  const isPdf = file?.type === "application/pdf";

  useEffect(() => {
    if (!file) return;
    const objUrl = URL.createObjectURL(file);
    setUrl(objUrl);
    return () => URL.revokeObjectURL(objUrl);
  }, [file]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  if (!file || !url) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex flex-col"
      style={{ background: "rgba(0,0,0,0.92)" }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-5 py-3 shrink-0"
        style={{
          background: "rgba(0,0,0,0.6)",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: isImage ? "#CBFBF1" : "#FEF3C7" }}
          >
            {isImage ? (
              <svg
                className="w-4 h-4"
                style={{ color: "#009689" }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            ) : (
              <svg
                className="w-4 h-4"
                style={{ color: "#d97706" }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
            )}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white truncate">
              {file.name}
            </p>
            <p className="text-xs" style={{ color: "rgba(255,255,255,0.45)" }}>
              {formatFileSize(file.size)}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="w-8 h-8 rounded-full flex items-center justify-center transition-colors shrink-0 ml-4"
          style={{
            background: "rgba(255,255,255,0.12)",
            color: "rgba(255,255,255,0.7)",
          }}
          onMouseOver={(e) =>
            (e.currentTarget.style.background = "rgba(255,255,255,0.22)")
          }
          onMouseOut={(e) =>
            (e.currentTarget.style.background = "rgba(255,255,255,0.12)")
          }
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Content — click backdrop to close */}
      <div className="flex-1 overflow-hidden" onClick={onClose}>
        {isImage && (
          <img
            src={url}
            alt={file.name}
            className="w-full h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        )}
        {isPdf && (
          <iframe
            src={url}
            title={file.name}
            className="w-full h-full border-0"
            style={{ background: "white" }}
            onClick={(e) => e.stopPropagation()}
          />
        )}
      </div>
    </div>,
    document.body,
  );
};

const Step1 = ({ form, onChange, onSelectProvince, onSelectCity, errors }) => {
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingProvince, setLoadingProvince] = useState(false);
  const [loadingCity, setLoadingCity] = useState(false);
  const debounceRef = useRef({});

  const searchProvinces = useCallback((prefix) => {
    clearTimeout(debounceRef.current.province);
    debounceRef.current.province = setTimeout(async () => {
      setLoadingProvince(true);
      try {
        const res = await getProvinces(prefix, 10);
        setProvinces(res.data?.data || []);
      } catch {
        setProvinces([]);
      } finally {
        setLoadingProvince(false);
      }
    }, 300);
  }, []);

  const searchCities = useCallback(
    (prefix) => {
      if (!form.provinceId) return;
      clearTimeout(debounceRef.current.city);
      debounceRef.current.city = setTimeout(async () => {
        setLoadingCity(true);
        try {
          const res = await getCities(form.provinceId, prefix, 10);
          setCities(res.data?.data || []);
        } catch {
          setCities([]);
        } finally {
          setLoadingCity(false);
        }
      }, 300);
    },
    [form.provinceId],
  );

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      <div className="sm:col-span-2">
        <Field label="Nama Perumahan" error={errors.name} required>
          <Input
            name="name"
            value={form.name}
            onChange={onChange}
            placeholder="Contoh: Perumahan Griya Asri"
            error={errors.name}
          />
        </Field>
      </div>
      <div className="sm:col-span-2">
        <Field label="Deskripsi" error={errors.description} required>
          <Textarea
            name="description"
            value={form.description}
            onChange={onChange}
            placeholder="Deskripsi singkat tentang perumahan..."
            error={errors.description}
          />
        </Field>
      </div>
      <div className="sm:col-span-2">
        <Field label="Alamat Lengkap" error={errors.address} required>
          <Textarea
            name="address"
            value={form.address}
            onChange={onChange}
            placeholder="Jl. Contoh No. 1, RT 01/RW 01..."
            error={errors.address}
            rows={2}
          />
        </Field>
      </div>
      <SearchableSelect
        label="Provinsi"
        required
        error={errors.province}
        value={form.provinceName}
        placeholder="Ketik nama provinsi..."
        onSearch={searchProvinces}
        onSelect={(item) => {
          onSelectProvince(item);
          setCities([]);
        }}
        options={provinces}
        loading={loadingProvince}
      />
      <SearchableSelect
        label="Kota / Kabupaten"
        required
        error={errors.city}
        value={form.cityName}
        placeholder="Ketik nama kota..."
        onSearch={searchCities}
        onSelect={onSelectCity}
        options={cities}
        loading={loadingCity}
        disabled={!form.provinceId}
      />
      <Field label="Jumlah Unit" error={errors.unit} required>
        <Input
          name="unit"
          value={form.unit}
          onChange={onChange}
          placeholder="Contoh: 200 unit"
          error={errors.unit}
        />
      </Field>
      <Field label="Tipe Rumah" error={errors.houseType} required>
        <Input
          name="houseType"
          value={form.houseType}
          onChange={onChange}
          placeholder="Contoh: Type 36, Type 45"
          error={errors.houseType}
        />
      </Field>
      <div className="sm:col-span-2">
        <Field label="Kepemilikan Rekening BSI" required>
          <div className="grid grid-cols-2 gap-3">
            {/* Sudah */}
            <button
              type="button"
              onClick={() =>
                onChange({ target: { name: "bsiAccountOwned", value: true } })
              }
              className="flex flex-col items-start gap-2 p-4 rounded-2xl transition-all text-left"
              style={{
                border: `2px solid ${form.bsiAccountOwned ? "#00BBA7" : "#E2E8F0"}`,
                background: form.bsiAccountOwned ? "#F0FDFA" : "white",
              }}
            >
              <div className="flex items-center gap-2.5">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center transition-all shrink-0"
                  style={{
                    background: form.bsiAccountOwned ? "#00BBA7" : "#F8FAFC",
                    border: `2px solid ${form.bsiAccountOwned ? "#00BBA7" : "#E2E8F0"}`,
                  }}
                >
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke={form.bsiAccountOwned ? "white" : "#94A3B8"}
                    strokeWidth={2.5}
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <span
                  className="text-sm font-semibold"
                  style={{
                    color: form.bsiAccountOwned ? "#005F5A" : "#94A3B8",
                  }}
                >
                  Sudah
                </span>
              </div>
              <span
                className="text-xs leading-relaxed"
                style={{ color: form.bsiAccountOwned ? "#009689" : "#94A3B8" }}
              >
                Perumahan sudah memiliki rekening BSI aktif
              </span>
              {form.bsiAccountOwned && (
                <span
                  className="text-xs font-semibold px-2.5 py-0.5 rounded-full"
                  style={{
                    background: "#CBFBF1",
                    color: "#005F5A",
                    border: "1.5px solid #96F7E4",
                  }}
                >
                  Dipilih
                </span>
              )}
            </button>

            {/* Belum */}
            <button
              type="button"
              onClick={() =>
                onChange({ target: { name: "bsiAccountOwned", value: false } })
              }
              className="flex flex-col items-start gap-2 p-4 rounded-2xl transition-all text-left"
              style={{
                border: `2px solid ${!form.bsiAccountOwned ? "#FCA5A5" : "#E2E8F0"}`,
                background: !form.bsiAccountOwned ? "#FFF5F5" : "white",
              }}
            >
              <div className="flex items-center gap-2.5">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center transition-all shrink-0"
                  style={{
                    background: !form.bsiAccountOwned ? "#EF4444" : "#F8FAFC",
                    border: `2px solid ${!form.bsiAccountOwned ? "#EF4444" : "#E2E8F0"}`,
                  }}
                >
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke={!form.bsiAccountOwned ? "white" : "#94A3B8"}
                    strokeWidth={2.5}
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>
                <span
                  className="text-sm font-semibold"
                  style={{
                    color: !form.bsiAccountOwned ? "#B91C1C" : "#334155",
                  }}
                >
                  Belum
                </span>
              </div>
              <span
                className="text-xs leading-relaxed"
                style={{ color: !form.bsiAccountOwned ? "#EF4444" : "#64748B" }}
              >
                Perumahan belum memiliki rekening BSI
              </span>
              {!form.bsiAccountOwned && (
                <span
                  className="text-xs font-semibold px-2.5 py-0.5 rounded-full"
                  style={{
                    background: "#FEE2E2",
                    color: "#B91C1C",
                    border: "1.5px solid #FCA5A5",
                  }}
                >
                  Dipilih
                </span>
              )}
            </button>
          </div>
        </Field>
      </div>
      <div className="sm:col-span-2">
        <Field label="Pesan Sambutan" error={errors.welcomeMessage}>
          <Textarea
            name="welcomeMessage"
            value={form.welcomeMessage}
            onChange={onChange}
            placeholder="Selamat datang di..."
          />
        </Field>
      </div>
    </div>
  );
};

const Step2 = ({
  form,
  onChange,
  onFileChange,
  onFileArrayChange,
  onAddFileArray,
  onRemoveFileArray,
  errors,
}) => (
  <div className="flex flex-col gap-5">
    <FileInput
      required
      label="Logo Perumahan"
      accept="image/*"
      error={errors.logoFile}
      file={form.logoFile}
      hint="Format: JPG, PNG — Maks. 5 MB"
      onChange={(e) => onFileChange("logoFile", e.target.files[0])}
    />
    <FileInput
      label="File NPWP (Opsional)"
      accept="image/*"
      error={errors.npwpFile}
      file={form.npwpFile}
      hint="Format: JPG, PNG — Maks. 5 MB"
      onChange={(e) => onFileChange("npwpFile", e.target.files[0])}
    />

    <div
      className="flex flex-col gap-3 rounded-xl p-4"
      style={{ background: "#F0FDFA", border: "1.5px solid #CBFBF1" }}
    >
      <p
        className="text-xs font-bold uppercase tracking-wider"
        style={{ color: "#009689" }}
      >
        📍 Lokasi Perumahan
      </p>
      <Field label="URL Google Maps" error={errors.mapUrl}>
        <Input
          name="mapUrl"
          value={form.mapUrl}
          onChange={onChange}
          placeholder="https://maps.google.com/..."
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Latitude" error={errors.latitude}>
          <Input
            name="latitude"
            type="number"
            value={form.latitude}
            onChange={onChange}
            placeholder="-6.200000"
          />
        </Field>
        <Field label="Longitude" error={errors.longitude}>
          <Input
            name="longitude"
            type="number"
            value={form.longitude}
            onChange={onChange}
            placeholder="106.816666"
          />
        </Field>
      </div>
      <p className="text-xs" style={{ color: "#00BBA7" }}>
        💡 Buka Google Maps → klik lokasi → salin koordinat & URL dari address
        bar
      </p>
    </div>

    <div className="flex flex-col gap-3">
      <label className="text-sm font-semibold" style={{ color: "#005F5A" }}>
        Foto Perumahan <span style={{ color: "#00BBA7" }}>*</span>
      </label>
      {form.imgFiles.map((file, i) => (
        <div key={i} className="flex gap-2 items-start">
          <div className="flex-1">
            <FileInput
              required
              label={`Foto ${i + 1}`}
              accept="image/*"
              error={i === 0 ? errors.imgFiles : undefined}
              file={file}
              hint="Format: JPG, PNG — Maks. 5 MB"
              onChange={(e) =>
                onFileArrayChange("imgFiles", i, e.target.files[0])
              }
            />
          </div>
          {form.imgFiles.length > 1 && (
            <button
              type="button"
              onClick={() => onRemoveFileArray("imgFiles", i)}
              className="mt-6 text-red-400 hover:text-red-600 text-xl transition-colors shrink-0 px-1"
            >
              ×
            </button>
          )}
        </div>
      ))}
      {errors.imgFiles && (
        <p className="text-xs text-red-500">{errors.imgFiles}</p>
      )}
      <button
        type="button"
        onClick={() => onAddFileArray("imgFiles")}
        className="self-start text-xs font-semibold px-3 py-1.5 rounded-lg transition-all"
        style={{
          color: "#009689",
          background: "#F0FDFA",
          border: "1.5px solid #96F7E4",
        }}
        onMouseOver={(e) => {
          e.target.style.borderColor = "#00BBA7";
          e.target.style.color = "#005F5A";
        }}
        onMouseOut={(e) => {
          e.target.style.borderColor = "#96F7E4";
          e.target.style.color = "#009689";
        }}
      >
        + Tambah Foto
      </button>
    </div>

    <div className="flex flex-col gap-3">
      <label className="text-sm font-semibold" style={{ color: "#005F5A" }}>
        Dokumen Legal <span style={{ color: "#00BBA7" }}>*</span>
      </label>
      {form.legalDocFiles.map((file, i) => (
        <div key={i} className="flex gap-2 items-start">
          <div className="flex-1">
            <FileInput
              required
              label={`Dokumen ${i + 1}`}
              accept=".pdf"
              error={i === 0 ? errors.legalDocFiles : undefined}
              file={file}
              hint="Format: PDF — Maks. 5 MB"
              onChange={(e) =>
                onFileArrayChange("legalDocFiles", i, e.target.files[0])
              }
            />
          </div>
          {form.legalDocFiles.length > 1 && (
            <button
              type="button"
              onClick={() => onRemoveFileArray("legalDocFiles", i)}
              className="mt-6 text-red-400 hover:text-red-600 text-xl transition-colors shrink-0 px-1"
            >
              ×
            </button>
          )}
        </div>
      ))}
      {errors.legalDocFiles && (
        <p className="text-xs text-red-500">{errors.legalDocFiles}</p>
      )}
      <button
        type="button"
        onClick={() => onAddFileArray("legalDocFiles")}
        className="self-start text-xs font-semibold px-3 py-1.5 rounded-lg transition-all"
        style={{
          color: "#009689",
          background: "#F0FDFA",
          border: "1.5px solid #96F7E4",
        }}
        onMouseOver={(e) => {
          e.target.style.borderColor = "#00BBA7";
          e.target.style.color = "#005F5A";
        }}
        onMouseOut={(e) => {
          e.target.style.borderColor = "#96F7E4";
          e.target.style.color = "#009689";
        }}
      >
        + Tambah Dokumen
      </button>
    </div>
  </div>
);

const Step3 = ({ form, onChange, onFileChange, errors }) => (
  <div className="flex flex-col gap-5">
    <p
      className="text-sm font-medium rounded-xl px-4 py-3"
      style={{
        color: "#009689",
        background: "#CBFBF1",
        border: "1.5px solid #96F7E4",
      }}
    >
      Data PIC (Person In Charge) adalah penanggungjawab utama perumahan ini.
    </p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      <Field label="Nama Lengkap PIC" error={errors.picName} required>
        <Input
          name="picName"
          value={form.picName}
          onChange={onChange}
          placeholder="Nama lengkap"
          error={errors.picName}
        />
      </Field>
      <Field label="NIK PIC" error={errors.picNik} required>
        <Input
          name="picNik"
          value={form.picNik}
          onChange={onChange}
          placeholder="16 digit NIK"
          maxLength={16}
          error={errors.picNik}
        />
      </Field>
      <Field label="Email PIC" error={errors.picEmail} required>
        <Input
          name="picEmail"
          type="email"
          value={form.picEmail}
          onChange={onChange}
          placeholder="email@contoh.com"
          error={errors.picEmail}
        />
      </Field>
      <Field label="Nomor HP PIC" error={errors.picPhone} required>
        <Input
          name="picPhone"
          value={form.picPhone}
          onChange={onChange}
          placeholder="08xxxxxxxxxx"
          error={errors.picPhone}
        />
      </Field>
      <div className="sm:col-span-2">
        <FileInput
          required
          label="Foto KTP PIC"
          accept="image/*"
          error={errors.picIdImgFile}
          file={form.picIdImgFile}
          hint="Format: JPG, PNG — pastikan terbaca jelas. Maks. 5 MB"
          onChange={(e) => onFileChange("picIdImgFile", e.target.files[0])}
        />
      </div>
    </div>
  </div>
);

const FileThumbnail = ({ file, label }) => {
  const [url, setUrl] = useState(null);
  const [preview, setPreview] = useState(false);
  const isImage = file?.type?.startsWith("image/");
  const isPdf = file?.type === "application/pdf";

  useEffect(() => {
    if (!file || !isImage) return;
    const objUrl = URL.createObjectURL(file);
    setUrl(objUrl);
    return () => URL.revokeObjectURL(objUrl);
  }, [file, isImage]);

  if (!file) return null;

  return (
    <>
      <div
        className="flex items-center gap-3 rounded-xl p-3 cursor-pointer transition-all"
        style={{ background: "#F0FDFA", border: "1.5px solid #CBFBF1" }}
        onClick={() => setPreview(true)}
        onMouseOver={(e) => (e.currentTarget.style.borderColor = "#00BBA7")}
        onMouseOut={(e) => (e.currentTarget.style.borderColor = "#CBFBF1")}
      >
        <div
          className="w-12 h-12 rounded-lg overflow-hidden shrink-0 flex items-center justify-center"
          style={{ background: isPdf ? "#FEF3C7" : "#CBFBF1" }}
        >
          {isImage && url ? (
            <img
              src={url}
              alt={file.name}
              className="w-full h-full object-cover"
            />
          ) : isPdf ? (
            <svg
              className="w-7 h-7"
              style={{ color: "#d97706" }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 13h1a1.5 1.5 0 010 3H9v-3zm0 0v4m4-4h.5a1.5 1.5 0 010 3H13m3-3v4"
              />
            </svg>
          ) : (
            <svg
              className="w-6 h-6"
              style={{ color: "#009689" }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
              />
            </svg>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p
            className="text-xs font-semibold truncate"
            style={{ color: "#005F5A" }}
          >
            {label}
          </p>
          <p className="text-xs truncate mt-0.5" style={{ color: "#009689" }}>
            {file.name}
          </p>
          <p className="text-xs mt-0.5" style={{ color: "#96F7E4" }}>
            {formatFileSize(file.size)}
          </p>
        </div>
        <div
          className="shrink-0 flex items-center gap-1.5 text-xs font-semibold"
          style={{ color: "#00BBA7" }}
        >
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
            />
          </svg>
          Lihat
        </div>
      </div>
      {preview && (
        <PreviewModal file={file} onClose={() => setPreview(false)} />
      )}
    </>
  );
};

const Step4 = ({ form }) => {
  const rows = [
    ["Nama Perumahan", form.name],
    ["Deskripsi", form.description],
    ["Alamat", form.address],
    ["Provinsi", form.provinceName],
    ["Kota / Kabupaten", form.cityName],
    ["Unit / Tipe", `${form.unit} / ${form.houseType}`],
    [
      "Rekening BSI",
      form.bsiAccountOwned ? "Sudah dimiliki" : "Belum dimiliki",
    ],
    ["URL Maps", form.mapUrl || "-"],
    [
      "Koordinat",
      form.latitude && form.longitude
        ? `${form.latitude}, ${form.longitude}`
        : "-",
    ],
    ["Nama PIC", form.picName],
    ["NIK PIC", form.picNik],
    ["Email PIC", form.picEmail],
    ["HP PIC", form.picPhone],
  ];

  return (
    <div className="flex flex-col gap-5">
      <p
        className="text-sm font-medium rounded-xl px-4 py-3"
        style={{
          color: "#009689",
          background: "#CBFBF1",
          border: "1.5px solid #96F7E4",
        }}
      >
        Periksa kembali data di bawah sebelum mengirim. Klik{" "}
        <strong>Kirim Pendaftaran</strong> jika sudah benar.
      </p>

      {/* Info rows */}
      <div
        className="rounded-xl overflow-hidden"
        style={{ border: "1.5px solid #CBFBF1" }}
      >
        {rows.map(([label, value], i) => (
          <div
            key={label}
            className="flex gap-4 px-4 py-3 text-sm"
            style={{ background: i % 2 === 0 ? "#F0FDFA" : "white" }}
          >
            <span
              className="w-40 shrink-0 font-semibold"
              style={{ color: "#009689" }}
            >
              {label}
            </span>
            <span className="break-all" style={{ color: "#0B4F4A" }}>
              {value || "-"}
            </span>
          </div>
        ))}
      </div>

      {/* File previews */}
      <div>
        <p
          className="text-xs font-bold uppercase tracking-wider mb-3"
          style={{ color: "#009689" }}
        >
          📎 Pratinjau File yang Diunggah
        </p>
        <p className="text-xs mb-3" style={{ color: "#96F7E4" }}>
          Klik file untuk melihat pratinjau.
        </p>

        <div className="flex flex-col gap-2">
          {/* Single files */}
          {form.logoFile && (
            <FileThumbnail file={form.logoFile} label="Logo Perumahan" />
          )}
          {form.npwpFile && (
            <FileThumbnail file={form.npwpFile} label="File NPWP" />
          )}
          {form.picIdImgFile && (
            <FileThumbnail file={form.picIdImgFile} label="Foto KTP PIC" />
          )}

          {/* Foto perumahan */}
          {form.imgFiles.filter(Boolean).length > 0 && (
            <div>
              <p
                className="text-xs font-semibold mt-2 mb-1.5"
                style={{ color: "#005F5A" }}
              >
                Foto Perumahan ({form.imgFiles.filter(Boolean).length} file)
              </p>
              <div className="flex flex-col gap-2">
                {form.imgFiles.filter(Boolean).map((file, i) => (
                  <FileThumbnail key={i} file={file} label={`Foto ${i + 1}`} />
                ))}
              </div>
            </div>
          )}

          {/* Dokumen legal */}
          {form.legalDocFiles.filter(Boolean).length > 0 && (
            <div>
              <p
                className="text-xs font-semibold mt-2 mb-1.5"
                style={{ color: "#005F5A" }}
              >
                Dokumen Legal ({form.legalDocFiles.filter(Boolean).length} file)
              </p>
              <div className="flex flex-col gap-2">
                {form.legalDocFiles.filter(Boolean).map((file, i) => (
                  <FileThumbnail
                    key={i}
                    file={file}
                    label={`Dokumen ${i + 1}`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const RegisterPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  const saveDraft = useCallback((data) => {
    try {
      const serializable = {
        ...data,
        logoFile: null,
        npwpFile: null,
        imgFiles: [],
        legalDocFiles: [],
        picIdImgFile: null,
      };
      localStorage.setItem("registerDraft", JSON.stringify(serializable));
    } catch {}
  }, []);

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
      setForm((prev) => {
        const next = { ...prev, [name]: value };
        saveDraft(next);
        return next;
      });
    },
    [saveDraft],
  );

  // ─── File change with 5MB guard ───────────────────────────────────────────
  const handleFileChange = useCallback((name, file) => {
    if (!file) return;
    const sizeError = validateFileSize(file);
    if (sizeError) {
      setErrors((prev) => ({ ...prev, [name]: sizeError }));
      return;
    }
    setErrors((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
    setForm((prev) => ({ ...prev, [name]: file }));
  }, []);

  const handleFileArrayChange = useCallback((field, index, file) => {
    if (!file) return;
    const sizeError = validateFileSize(file);
    if (sizeError) {
      setErrors((prev) => ({ ...prev, [field]: sizeError }));
      return;
    }
    setForm((prev) => {
      const arr = [...prev[field]];
      arr[index] = file;
      return { ...prev, [field]: arr };
    });
    setErrors((prev) => {
      const n = { ...prev };
      delete n[field];
      return n;
    });
  }, []);

  const handleAddFileArray = useCallback((field) => {
    setForm((prev) => ({ ...prev, [field]: [...prev[field], null] }));
  }, []);

  const handleRemoveFileArray = useCallback((field, index) => {
    setForm((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  }, []);

  const handleSelectProvince = useCallback((item) => {
    setForm((prev) => ({
      ...prev,
      provinceId: item.id,
      provinceName: item.name,
      cityId: "",
      cityName: "",
    }));
    setErrors((prev) => {
      const n = { ...prev };
      delete n.province;
      delete n.city;
      return n;
    });
  }, []);

  const handleSelectCity = useCallback((item) => {
    setForm((prev) => ({ ...prev, cityId: item.id, cityName: item.name }));
    setErrors((prev) => {
      const n = { ...prev };
      delete n.city;
      return n;
    });
  }, []);

  const handleNext = () => {
    const stepErrors = validateStep(step, form);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }
    setErrors({});
    setStep((s) => s + 1);
  };

  const handleBack = () => {
    setErrors({});
    setStep((s) => s - 1);
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setSubmitError("");
    const payload = {
      ...form,
      city: form.cityName,
      province: form.provinceName,
      longitude: form.longitude ? parseFloat(form.longitude) : 0,
      latitude: form.latitude ? parseFloat(form.latitude) : 0,
      imgFiles: form.imgFiles.filter(Boolean),
      legalDocFiles: form.legalDocFiles.filter(Boolean),
    };
    try {
      await registerHousing(payload);
      localStorage.removeItem("registerDraft");
      setIsSuccess(true);
    } catch (err) {
      setSubmitError(
        err.userMessage ||
          "Gagal mengirim data. Periksa koneksi Anda dan coba lagi.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-6"
        style={{
          background:
            "linear-gradient(145deg, #0B4F4A 0%, #005F5A 20%, #009689 50%, #00BBA7 75%, #009689 100%)",
        }}
      >
        <div className="max-w-md w-full text-center">
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
            style={{ background: "linear-gradient(135deg, #CBFBF1, #96F7E4)" }}
          >
            <svg
              className="w-10 h-10"
              style={{ color: "#009689" }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold mb-3" style={{ color: "white" }}>
            Pendaftaran Berhasil!
          </h2>
          <p className="mb-8" style={{ color: "#CBFBF1" }}>
            Terima kasih telah mendaftarkan perumahan Anda di BWarga. Tim kami
            akan segera memverifikasi kelengkapan data Anda. Pantau terus kotak
            masuk email PIC yang terdaftar untuk langkah selanjutnya, ya!
          </p>
          <button
            onClick={() => navigate("/")}
            className="text-white px-8 py-3 rounded-xl font-semibold transition-all hover:opacity-90 hover:shadow-lg"
            style={{ background: "linear-gradient(135deg, #00D5BE, #009689)" }}
          >
            Kembali ke Beranda
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        background:
          "linear-gradient(145deg, #0B4F4A 0%, #005F5A 20%, #009689 50%, #00BBA7 75%, #009689 100%)",
      }}
    >
      {/* Decorative blobs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full opacity-25"
          style={{
            background: "radial-gradient(circle, #00D5BE 0%, transparent 65%)",
          }}
        />
        <div
          className="absolute top-1/3 -left-40 w-[400px] h-[400px] rounded-full opacity-15"
          style={{
            background: "radial-gradient(circle, #96F7E4 0%, transparent 65%)",
          }}
        />
        <div
          className="absolute -bottom-40 right-1/4 w-[500px] h-[500px] rounded-full opacity-20"
          style={{
            background: "radial-gradient(circle, #0B4F4A 0%, transparent 65%)",
          }}
        />
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.4) 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />
      </div>

      <header
        className="sticky top-0 z-20 shadow-sm"
        style={{
          background: "rgba(0,150,137,0.85)",
          backdropFilter: "blur(12px)",
          borderBottom: "1.5px solid rgba(0,213,190,0.3)",
        }}
      >
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm font-medium transition-colors"
            style={{ color: "#CBFBF1" }}
            onMouseOver={(e) => (e.currentTarget.style.color = "white")}
            onMouseOut={(e) => (e.currentTarget.style.color = "#CBFBF1")}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Kembali
          </button>
          <span
            className="font-bold text-sm tracking-wide"
            style={{ color: "white" }}
          >
            Daftar Perumahan
          </span>
          <span
            className="text-xs font-bold px-2.5 py-1 rounded-full"
            style={{
              color: "#009689",
              background: "#F0FDFA",
              border: "1.5px solid #CBFBF1",
            }}
          >
            {step}/{TOTAL_STEPS}
          </span>
        </div>
      </header>

      <div
        className="sticky top-[57px] z-20 h-1.5"
        style={{ background: "rgba(0,95,90,0.4)" }}
      >
        <div
          className="h-full transition-all duration-500 ease-out"
          style={{
            width: `${(step / TOTAL_STEPS) * 100}%`,
            background: "linear-gradient(90deg, #00D5BE, #00BBA7)",
          }}
        />
      </div>

      <main className="relative z-10 flex-1 flex items-start justify-center px-6 py-10">
        <div className="w-full max-w-2xl">
          <div
            className="sticky top-[63px] z-10 flex items-center gap-2 mb-6 py-3 px-4 rounded-2xl"
            style={{
              background: "rgba(0,150,137,0.75)",
              backdropFilter: "blur(12px)",
            }}
          >
            {STEP_TITLES.map((title, i) => (
              <React.Fragment key={title}>
                <div
                  className={`flex items-center gap-2 transition-opacity ${i + 1 === step ? "opacity-100" : i + 1 < step ? "opacity-80" : "opacity-40"}`}
                >
                  <div
                    className="w-7 h-7 rounded-full text-xs font-bold flex items-center justify-center transition-all"
                    style={
                      i + 1 <= step
                        ? {
                            background:
                              "linear-gradient(135deg, #00D5BE, #009689)",
                            color: "white",
                            boxShadow: "0 2px 8px rgba(0,187,167,0.3)",
                          }
                        : {
                            background: "#F0FDFA",
                            color: "#009689",
                            border: "2px solid #96F7E4",
                          }
                    }
                  >
                    {i + 1 < step ? (
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    ) : (
                      i + 1
                    )}
                  </div>
                  <span
                    className="text-xs font-semibold hidden sm:block"
                    style={{
                      color:
                        i + 1 === step
                          ? "white"
                          : i + 1 < step
                            ? "#CBFBF1"
                            : "rgba(203,251,241,0.4)",
                    }}
                  >
                    {title}
                  </span>
                </div>
                {i < TOTAL_STEPS - 1 && (
                  <div
                    className="flex-1 h-0.5 rounded-full transition-all"
                    style={{
                      background:
                        i + 1 < step ? "#F0FDFA" : "rgba(0,95,90,0.4)",
                    }}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          <div
            className="rounded-2xl p-6 sm:p-8"
            style={{
              background: "rgba(255,255,255,0.85)",
              backdropFilter: "blur(12px)",
              border: "1.5px solid #CBFBF1",
              boxShadow:
                "0 8px 32px rgba(0, 187, 167, 0.10), 0 2px 8px rgba(0,187,167,0.06)",
            }}
          >
            <div
              className="flex items-center gap-3 mb-6 pb-5"
              style={{ borderBottom: "1.5px solid #F0FDFA" }}
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                style={{
                  background: "linear-gradient(135deg, #CBFBF1, #96F7E4)",
                }}
              >
                <span
                  className="font-bold text-sm"
                  style={{ color: "#009689" }}
                >
                  {step}
                </span>
              </div>
              <div>
                <h2 className="text-lg font-bold" style={{ color: "#0B4F4A" }}>
                  {STEP_TITLES[step - 1]}
                </h2>
                <p className="text-xs" style={{ color: "#009689" }}>
                  {step === 1 && "Isi informasi dasar perumahan Anda."}
                  {step === 2 && "Tambahkan media dan dokumen pendukung."}
                  {step === 3 && "Data penanggung jawab perumahan."}
                  {step === 4 && "Tinjau semua data sebelum mengirim."}
                </p>
              </div>
            </div>

            {step === 1 && (
              <Step1
                form={form}
                onChange={handleChange}
                onSelectProvince={handleSelectProvince}
                onSelectCity={handleSelectCity}
                errors={errors}
              />
            )}
            {step === 2 && (
              <Step2
                form={form}
                onChange={handleChange}
                onFileChange={handleFileChange}
                onFileArrayChange={handleFileArrayChange}
                onAddFileArray={handleAddFileArray}
                onRemoveFileArray={handleRemoveFileArray}
                errors={errors}
              />
            )}
            {step === 3 && (
              <Step3
                form={form}
                onChange={handleChange}
                onFileChange={handleFileChange}
                errors={errors}
              />
            )}
            {step === 4 && <Step4 form={form} />}

            {submitError && (
              <div
                className="mt-5 flex items-start gap-3 rounded-xl px-4 py-3"
                style={{ background: "#FEF2F2", border: "1.5px solid #FECACA" }}
              >
                <svg
                  className="w-5 h-5 text-red-500 shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  <p className="text-sm text-red-600 font-medium">
                    {submitError}
                  </p>
                  <div className="flex gap-3 mt-1.5">
                    <button
                      onClick={handleSubmit}
                      className="text-xs text-red-500 hover:text-red-700 underline font-medium"
                    >
                      Coba lagi
                    </button>
                    <span className="text-red-300">·</span>
                    <button
                      onClick={() => setSubmitError("")}
                      className="text-xs text-red-400 hover:text-red-600"
                    >
                      Tutup
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div
              className="flex gap-3 mt-8 pt-6"
              style={{ borderTop: "1.5px solid #F0FDFA" }}
            >
              {step > 1 && (
                <button
                  onClick={handleBack}
                  disabled={isLoading}
                  className="flex-1 sm:flex-none sm:w-32 px-6 py-3 rounded-xl font-semibold text-sm transition-all disabled:opacity-50"
                  style={{
                    border: "2px solid #96F7E4",
                    color: "#009689",
                    background: "white",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = "#00BBA7";
                    e.currentTarget.style.background = "#F0FDFA";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = "#96F7E4";
                    e.currentTarget.style.background = "white";
                  }}
                >
                  ← Kembali
                </button>
              )}
              <button
                onClick={step < TOTAL_STEPS ? handleNext : handleSubmit}
                disabled={isLoading}
                className="flex-1 text-white px-6 py-3 rounded-xl font-semibold text-sm transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                style={{
                  background: "linear-gradient(135deg, #00D5BE, #009689)",
                  boxShadow: "0 4px 14px rgba(0,187,167,0.3)",
                }}
                onMouseOver={(e) => (e.currentTarget.style.opacity = "0.9")}
                onMouseOut={(e) => (e.currentTarget.style.opacity = "1")}
              >
                {isLoading ? (
                  <>
                    <svg
                      className="w-4 h-4 animate-spin"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    Mengirim...
                  </>
                ) : step < TOTAL_STEPS ? (
                  "Lanjut →"
                ) : (
                  "Kirim Pendaftaran"
                )}
              </button>
            </div>
          </div>

          <p
            className="text-center text-xs mt-4"
            style={{ color: "rgb(255, 255, 255)" }}
          >
            Data Anda tersimpan otomatis saat mengisi form ini.
          </p>
        </div>
      </main>
    </div>
  );
};

export default RegisterPage;
